import { useEffect, useRef, useState } from "react";
import {
  Divider,
  Modal as MuiModal,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import XMLViewer from "react-xml-viewer";
import FieldLoader from "../Atom/FieldLoader";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

type ContractProps = {
  tradKey: string;
  xmlUrl: string;
  xmlFileName: string;
  open: boolean;
  setOpen: Function;
};

export default function PaymentFileModal({
  tradKey,
  xmlUrl,
  xmlFileName,
  open,
  setOpen,
}: ContractProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    setOpen(false);
  };
  const [xmlContent, setXmlContent] = useState<string>("");

  const style = {
    bgcolor: "background.paper",
    border: "1px solid #000",
    borderRadius: "10px",
    boxShadow: 24,
    p: 3,
    gap: 2,
    width: "100%",
    height: "100%",
    overflowY: "scroll",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  };

  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      fetch(xmlUrl).then((res: any) => {
        res.text().then((content: any) => setXmlContent(content));
      });
    }
  }, [open]);

  return (
    <MuiModal open={open} onClose={handleClose} sx={{ padding: 2 }}>
      <Box sx={style} ref={modalRef}>
        <Box
          sx={{
            position: "fixed",
            float: "right",
            right: "3em",
          }}
          onClick={handleClose}
        >
          <CloseIcon fontSize="large" />
        </Box>

        <Typography fontWeight="bold" fontSize={25} color="success">
          {t(`CONTRACT.${tradKey}.label`)}
        </Typography>
        <Divider />
        <Typography>{t(`CONTRACT.${tradKey}.text`)}</Typography>

        <Box
          sx={{
            width: isMobile ? "100%" : "90%",
            alignSelf: "center",
          }}
        >
          {xmlContent === "" ? (
            <FieldLoader />
          ) : (
            <XmlViewer xmlContent={xmlContent} fileName={xmlFileName} />
          )}
        </Box>
      </Box>
    </MuiModal>
  );
}

type XmlViewerProps = {
  xmlContent: string;
  fileName: string;
};

function XmlViewer({ xmlContent, fileName }: XmlViewerProps) {
  const handleDownload = (xmlData: string) => {
    const blob = new Blob([xmlData], { type: "application/xml" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  return (
    <Box border={"1px solid black"} padding={2} position={"relative"}>
      <Box
        onClick={() => {
          handleDownload(xmlContent);
        }}
        sx={{
          position: "absolute",
          right: 0,
          marginRight: 2,
          "& > :hover": {
            color: "blue",
            borderRadius: "5px",
            border: "2px solid blue",
            cursor: "pointer",
          },
        }}
      >
        <FileDownloadIcon fontSize="large" />
      </Box>
      <XMLViewer xml={xmlContent} />
    </Box>
  );
}
